import { memo } from 'react'
import { Handle, Position, type Node } from '@xyflow/react'
import { Play, Clock, Webhook, FileText, X, Zap } from 'lucide-react'
import { cn } from '@/lib/utils'

export type TriggerType = 'form' | 'schedule' | 'webhook' | 'manual'
export type TriggerNodeStatus = 'idle' | 'configured' | 'active' | 'error'

export interface TriggerCanvasNodeData {
  label: string
  triggerType: TriggerType
  status?: TriggerNodeStatus
  errorMessage?: string
  formId?: string
  formName?: string
  cronExpression?: string
  scheduleDescription?: string
  webhookUrl?: string
  webhookSecret?: string
  onDelete?: () => void
  [key: string]: unknown
}

export type TriggerCanvasNode = Node<TriggerCanvasNodeData, 'trigger'>

const triggerTypeIcons: Record<TriggerType, React.ComponentType<{ className?: string }>> = {
  form: FileText,
  schedule: Clock,
  webhook: Webhook,
  manual: Play,
}

interface TriggerCanvasNodeProps {
  data: TriggerCanvasNodeData
  selected?: boolean
}

function TriggerCanvasNodeComponent({ data, selected }: TriggerCanvasNodeProps) {
  const Icon = triggerTypeIcons[data.triggerType] || Zap

  const getSubtitle = () => {
    switch (data.triggerType) {
      case 'form':
        return data.formName || 'Select a form'
      case 'schedule':
        return data.scheduleDescription || data.cronExpression || 'Set schedule'
      case 'webhook':
        return data.webhookUrl ? 'Webhook configured' : 'Set webhook URL'
      case 'manual':
        return 'Click to start workflow'
      default:
        return 'Configure trigger'
    }
  }

  return (
    <div
      className={cn(
        'group relative bg-white dark:bg-gray-800 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 min-w-[200px] max-w-[240px] overflow-hidden',
        'border border-gray-200 dark:border-gray-700',
        selected && 'ring-2 ring-blue-500 ring-offset-2 dark:ring-offset-gray-900 border-blue-500'
      )}
    >
      {/* Left color accent bar */}
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-500" />

      {/* Delete button */}
      <button
        onClick={(e) => {
          e.stopPropagation()
          data.onDelete?.()
        }}
        className="absolute top-2 right-2 p-1 rounded-md opacity-0 group-hover:opacity-100 hover:bg-red-50 dark:hover:bg-red-900/30 text-gray-400 hover:text-red-500 transition-all"
      >
        <X className="h-3.5 w-3.5" />
      </button>

      {/* Node Content */}
      <div className="pl-4 pr-3 py-3">
        <div className="flex items-start gap-2.5">
          <div className="flex-shrink-0 mt-0.5">
            <div className="p-1.5 rounded-lg bg-blue-100 dark:bg-blue-900/40">
              <Icon className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
          <div className="min-w-0 flex-1 pr-4">
            <p className="font-medium text-gray-900 dark:text-white text-sm leading-tight">
              {data.label || 'Start'}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
              {getSubtitle()}
            </p>
          </div>
        </div>

        {/* Error message */}
        {data.status === 'error' && data.errorMessage && (
          <div className="mt-2 p-2 rounded-lg bg-red-50 dark:bg-red-900/20">
            <p className="text-xs text-red-600 dark:text-red-400 line-clamp-2">
              {data.errorMessage}
            </p>
          </div>
        )}
      </div>

      {/* Output Handle (no input for trigger - it's the start) */}
      <Handle
        type="source"
        position={Position.Bottom}
        className="!w-2.5 !h-2.5 !bg-emerald-500 !border-2 !border-white dark:!border-gray-800 !-bottom-1.5"
      />
    </div>
  )
}

export default memo(TriggerCanvasNodeComponent)
